import { useEffect, useReducer, useRef } from "react";

import type { AgenticAiState } from "@lib/agenticAi";
import { agenticAiStore } from "@lib/agenticAi";

/**
 * Selects Agentic AI state without requiring a React-Redux Provider.
 *
 * The dashboard uses React 17, so this manually implements the relevant
 * useSyncExternalStore behavior: it resynchronizes after subscribing to cover
 * changes between render and effect setup, and only updates React state when
 * the selected value changes.
 */
export const useAgenticAiExternalStoreSelector = <Selected>(
  selector: (state: AgenticAiState) => Selected,
  enabled = true
): Selected => {
  const [, forceRender] = useReducer((version: number) => version + 1, 0);
  const selected = selector(agenticAiStore.getState().agenticAi);
  const selectedRef = useRef(selected);
  const selectorRef = useRef(selector);
  selectedRef.current = selected;
  selectorRef.current = selector;

  useEffect(() => {
    if (!enabled) {
      return;
    }

    const updateSelected = () => {
      const nextSelected = selectorRef.current(agenticAiStore.getState().agenticAi);
      if (Object.is(nextSelected, selectedRef.current)) {
        return;
      }

      selectedRef.current = nextSelected;
      forceRender();
    };

    updateSelected();
    return agenticAiStore.subscribe(updateSelected);
  }, [enabled]);

  return selected;
};
